import { CatalogItem, CourseCatalogItemLimited, CourseDetails, CourseOutlineNode } from '../common/types';

/**
 * Helper to map from external models into an internal CourseDetails model.
 */
const DATA_NOT_AVAILABLE = '';

interface CatalogServiceOutlineNode {
    id: string;
    name: string;
    children: CatalogServiceOutlineNode[];
}

/**
 * Parse and transform the outline from API format to internal format.
 * API format has {id, name, children[]} structure.
 * Internal format has {header, details, nodes[]} structure.
 * @param outlineString Stringified JSON outline from API
 * @returns CourseOutline object
 */
const parseOutline = (outlineString: string | undefined): { nodes?: CourseOutlineNode[] } => {
    if (!outlineString) {
        return { nodes: undefined };
    }

    try {
        const serviceOutline: CatalogServiceOutlineNode[] = JSON.parse(outlineString);
        const rootNode = serviceOutline[0];
        if (!rootNode?.children?.length) {
            return { nodes: undefined };
        }

        // Transform day nodes into our format
        const nodes: CourseOutlineNode[] = rootNode.children.map((dayNode: CatalogServiceOutlineNode) => ({
            header: dayNode.name, // "Day 1", "Day 2", etc.
            nodes: dayNode.children.map((moduleNode: CatalogServiceOutlineNode) => ({
                header: moduleNode.name, // "Module 1: ...", etc.
                details: moduleNode.children.map((item: CatalogServiceOutlineNode) => item.name), // Module content items
            })),
        }));

        return { nodes };
    } catch (error) {
        // If parsing fails, return undefined nodes
        console.error('Failed to parse course outline:', error);
        return { nodes: undefined };
    }
};

export class CourseDetailsMapper {
    /**
     * Map a CatalogItem into a CourseDetails object.
     * @param catalogItem CatalogItem to map from.
     * @returns CourseDetails object
     */
    public static fromCatalogItem(catalogItem: CatalogItem): CourseDetails {
        // Fallback to first child's outline if course outline is undefined
        const outlineString = catalogItem.outline ?? catalogItem.children?.nodes?.[0]?.outline;

        return {
            catalogItemId: catalogItem.id,
            name: catalogItem.name,
            description: catalogItem.description || DATA_NOT_AVAILABLE,
            duration: this.fromCatalogItemDuration(catalogItem),
            category: catalogItem.domain?.valueDisplayString || DATA_NOT_AVAILABLE,
            skillLevel: catalogItem.skillLevel?.valueDisplayString || DATA_NOT_AVAILABLE,
            objectives: catalogItem.objectives?.values || [],
            audience: catalogItem.intendedAudience?.values || [],
            prerequisites: catalogItem.prerequisites?.value || 'None required',
            outline: parseOutline(outlineString),
        };
    }

    /**
     * Map a CourseCatalogItemLimited into a CourseDetails object.
     * Used for ADC link sharing where outline and children are not available.
     * @param item CourseCatalogItemLimited to map from.
     * @returns CourseDetails object with empty outline
     */
    public static fromCatalogItemLimited(item: CourseCatalogItemLimited): CourseDetails {
        return {
            catalogItemId: item.id,
            name: item.name,
            description: item.description || DATA_NOT_AVAILABLE,
            duration: this.fromCatalogItemDuration(item),
            category: item.domain?.valueDisplayString || DATA_NOT_AVAILABLE,
            skillLevel: item.skillLevel?.valueDisplayString || DATA_NOT_AVAILABLE,
            objectives: item.objectives?.values || [],
            audience: item.intendedAudience?.values || [],
            prerequisites: item.prerequisites?.value || 'None required',
            outline: { nodes: undefined },
        };
    }

    /**
     * Convert the CatalogItem duration into a human-readable string.
     * https://code.amazon.com/packages/AwsTcCatalogService/blobs/a55f872d4e2c9e471ea6a20cbdf7ff06c5d7b8fa/--/src/api/schema.graphql#L1111
     * @param catalogItem Catalog Item that contains the duration.
     * @returns duration as a string or undefined if we cannot map the value.
     */
    public static fromCatalogItemDuration(catalogItem: CatalogItem | CourseCatalogItemLimited): string | undefined {
        const { value, unit } = catalogItem.duration;
        switch (unit) {
            case 'HOURS':
                return `${value} ${this.pluralize(value, 'hour')}`;
            case 'MINUTES':
                return `${value} ${this.pluralize(value, 'minute')}`;
            case 'DAYS':
                return `${value} ${this.pluralize(value, 'day')}`;
            case 'SECONDS':
                return `${value} ${this.pluralize(value, 'second')}`;
            default:
                // TODO: Would like some logging / error handling here.
                return undefined;
        }
    }

    // Helper to pluralize units.
    private static pluralize(value: number, unit: string): string {
        return `${unit}` + (value > 1 ? 's' : '');
    }
}
